import React from "react";
import { Box, CircularProgress, IconButton, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import MicRounded from "@mui/icons-material/MicRounded";
import StopRounded from "@mui/icons-material/StopRounded";

/** The one size of the record button, on the desktop and the phone alike. */
export const RECORD_BUTTON_SIZE = 120;

/**
 * The big circular mic/stop button, shared by ChartMind's Record step and the phone page so the
 * two look and behave the same.
 *
 * `level` (0–1, from useEncounterRecording's micLevel) drives a soft halo behind the button that
 * swells with the voice while recording, the way current voice assistants show they are hearing
 * you. Without a level (the desktop showing a recording made on the phone) it pulses instead.
 *
 * `busy` keeps the button's colour and puts a spinner inside it, so "working on it" reads as the
 * same control rather than a greyed-out one.
 */
const RecordButton = ({
  recording = false,
  busy = false,
  disabled = false,
  onClick,
  size = RECORD_BUTTON_SIZE,
  ariaLabel,
  level,
  // "continue" is the Events-note-type start control (arrow); same size/chrome as the mic.
  variant = "mic",
}) => {
  const theme = useTheme();
  const continueMode = variant === "continue";
  // contrastText rather than white: dark mode's primary is a light blue that white
  // disappears on. Both resolve to white in light mode.
  const iconColor = recording && !continueMode
    ? theme.palette.error.contrastText
    : theme.palette.primary.contrastText;
  const base = recording && !continueMode ? theme.palette.error.main : theme.palette.primary.main;
  const showHalo = !continueMode && recording && !busy && typeof level === "number";
  const pulse = !continueMode && recording && !busy && !showHalo;

  return (
    <Box sx={{ position: "relative", width: size, height: size, display: "inline-flex" }}>
      {showHalo && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            bgcolor: alpha(theme.palette.error.main, 0.18),
            transform: `scale(${1 + Math.min(1, Math.max(0, level)) * 0.35})`,
            transition: "transform 90ms linear",
          }}
        />
      )}
      <IconButton
        onClick={onClick}
        aria-label={
          ariaLabel ||
          (continueMode ? "Continue" : recording ? "Stop recording" : "Start recording")
        }
        disabled={disabled || busy}
        sx={{
          position: "relative",
          width: size,
          height: size,
          backgroundColor: base,
          color: iconColor,
          transition: "all 0.3s ease",
          "&:hover": {
            backgroundColor:
              recording && !continueMode ? theme.palette.error.dark : theme.palette.primary.dark,
            transform: "scale(1.05)",
          },
          "&:active": {
            transform: "scale(0.95)",
          },
          "&.Mui-disabled": busy
            ? { backgroundColor: base, color: iconColor }
            : {
                backgroundColor:
                  theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[400],
                color: theme.palette.common.white,
              },
          boxShadow: recording
            ? `0 0 0 0 ${theme.palette.error.main}99`
            : `0 8px 20px ${theme.palette.primary.main}40`,
          animation: pulse ? "pulse 2s infinite" : "none",
          "@keyframes pulse": {
            "0%": { boxShadow: `0 0 0 0 ${theme.palette.error.main}99` },
            "70%": { boxShadow: `0 0 0 ${size * 0.19}px ${theme.palette.error.main}00` },
            "100%": { boxShadow: `0 0 0 0 ${theme.palette.error.main}00` },
          },
        }}
      >
        {busy ? (
          <CircularProgress size={size * 0.4} sx={{ color: iconColor }} />
        ) : continueMode ? (
          <ArrowForwardRounded sx={{ fontSize: size * 0.5 }} />
        ) : recording ? (
          <StopRounded sx={{ fontSize: size * 0.5 }} />
        ) : (
          <MicRounded sx={{ fontSize: size * 0.5 }} />
        )}
      </IconButton>
    </Box>
  );
};

export default RecordButton;
